'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Skeleton } from '@material-bank/mb3-components';
import { useAnalyticsContextSafe } from '@material-bank/mtag-analytics';
import { GalleryImageLabel } from './GalleryImageLabel';
import { areImagesWarm, formatImageSource, markImagesWarm } from '@material-bank/mtag-utilities';
import { useOverscrollBounce } from './useOverscrollBounce';
import { PHOTO_CAROUSEL_SWIPE_ANALYTICS_ID } from './analyticsIds';
const SWIPE_SETTLE_MS = 150;
const SWIPE_MIN_DELTA_PX = 40;
// A swipe only counts if a touch/pointer gesture happened within this window before the
// scroll settled, so scroll restoration, momentum from a prior gesture, and programmatic
// scrolls don't register as swipes the user never performed.
const SWIPE_GESTURE_WINDOW_MS = 1500;

// One helper for the rendered `src`/`srcSet` and the warm-record key, so the
// record answers for exactly the bytes the <img> asks for.
function imageSource(item: string) {
  return formatImageSource(item, 400, false);
}
type PeekGalleryProps = {
  items: string[];
  onItemClick: (index: number) => void;
};
export function PeekGallery(t0) {
  const $ = _c(28);
  const {
    items,
    onItemClick
  } = t0;
  const isSingleItem = items.length === 1;
  const scrollRef = useRef(null);
  const x = useOverscrollBounce(scrollRef, !isSingleItem);
  const analytics = useAnalyticsContextSafe();
  const trackSwipeRef = useRef(analytics?.trackRegisteredClick);
  let t1;
  if ($[0] !== analytics?.trackRegisteredClick) {
    t1 = () => {
      trackSwipeRef.current = analytics?.trackRegisteredClick;
    };
    $[0] = analytics?.trackRegisteredClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== analytics) {
    t2 = [analytics];
    $[2] = analytics;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== isSingleItem) {
    t3 = () => {
      const node = scrollRef.current;
      if (!node || isSingleItem) {
        return;
      }
      let settledScrollLeft = node.scrollLeft;
      let settleTimer;
      let lastGestureAt = 0;
      const markGesture = function markGesture() {
        lastGestureAt = Date.now();
      };
      const handleScroll = function handleScroll() {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          if (!node) {
            return;
          }
          const movedEnough = Math.abs(node.scrollLeft - settledScrollLeft) >= SWIPE_MIN_DELTA_PX;
          const fromUserGesture = Date.now() - lastGestureAt <= SWIPE_GESTURE_WINDOW_MS;
          if (movedEnough && fromUserGesture) {
            trackSwipeRef.current?.(PHOTO_CAROUSEL_SWIPE_ANALYTICS_ID);
          }
          settledScrollLeft = node.scrollLeft;
        }, SWIPE_SETTLE_MS);
      };
      node.addEventListener("pointerdown", markGesture, {
        passive: true
      });
      node.addEventListener("touchstart", markGesture, {
        passive: true
      });
      node.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        clearTimeout(settleTimer);
        node.removeEventListener("pointerdown", markGesture);
        node.removeEventListener("touchstart", markGesture);
        node.removeEventListener("scroll", handleScroll);
      };
    };
    t4 = [isSingleItem];
    $[4] = isSingleItem;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== items) {
    t5 = () => new Set(items.filter(_temp));
    $[7] = items;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [settledImages, setSettledImages] = useState(t5);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (item_0, loaded) => {
      if (loaded) {
        markImagesWarm([imageSource(item_0)]);
      }
      setSettledImages(prev => {
        if (prev.has(item_0)) {
          return prev;
        }
        return new Set(prev).add(item_0);
      });
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const markSettled = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = el => {
      if (!el?.complete || !el.currentSrc) {
        return;
      }
      const item_1 = el.dataset.item;
      if (!item_1) {
        return;
      }
      markSettled(item_1, el.naturalWidth > 0);
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const imageRef = t7;
  let t8;
  if ($[11] !== x) {
    t8 = {
      x
    };
    $[11] = x;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = `flex gap-3 h-full overflow-x-auto snap-x snap-mandatory [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isSingleItem ? "px-6" : "pl-6 pr-6 scroll-pl-6"}`;
  let t10;
  if ($[13] !== isSingleItem || $[14] !== items || $[15] !== onItemClick || $[16] !== settledImages) {
    let t11;
    if ($[18] !== isSingleItem || $[19] !== onItemClick || $[20] !== settledImages) {
      t11 = (item_2, index) => <article key={item_2} className={`h-full ${isSingleItem ? "w-full" : "max-w-[clamp(280px,85vw,400px)] snap-start shrink-0 w-[85%]"} cursor-pointer select-none rounded-2xl`} data-analyticsid="product_card_photo_carousel_open_gallery_view" data-haptic="light" onClick={() => onItemClick(index)}><div className="relative w-full flex items-center justify-center min-h-[170px] h-full">{settledImages.has(item_2) ? null : <Skeleton className="absolute inset-0 h-full w-full rounded-2xl" data-testid="peek-gallery-image-skeleton" />}<img ref={imageRef} data-item={item_2} alt="" draggable={false} className="relative h-full w-full object-cover rounded-2xl" {...index === 0 ? {
            fetchPriority: "high" as const
          } : {
            loading: "lazy" as const
          }} onLoad={() => markSettled(item_2, true)} onError={() => markSettled(item_2, false)} {...imageSource(item_2)} /><GalleryImageLabel labelType={null} /></div></article>;
      $[18] = isSingleItem;
      $[19] = onItemClick;
      $[20] = settledImages;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    t10 = items.map(t11);
    $[13] = isSingleItem;
    $[14] = items;
    $[15] = onItemClick;
    $[16] = settledImages;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <div ref={scrollRef} className={t9}>{t10}</div>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t8) {
    t12 = <section className="w-full h-full min-h-[180px]"><motion.div className="h-full" style={t8}>{t11}</motion.div></section>;
    $[25] = t11;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function _temp(item) {
  return areImagesWarm([imageSource(item)]);
}